import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { AuthPageErrorBoundary } from '@/components/auth/AuthPageErrorBoundary';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ForgotPasswordPage() {
  return (
    <AuthPageErrorBoundary pageName="forgot password">
      <ForgotPasswordContent />
    </AuthPageErrorBoundary>
  );
}

function ForgotPasswordContent() {
  const { forgotPassword, error } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      const result = await forgotPassword(email);
      
      console.log('Forgot password result:', result);
      
      if (result.error) {
        // Error will be handled by the AuthProvider and shown via the error prop
        console.error('Password reset request failed:', result.error);
      } else if (result.data?.message) {
        // Success: API returned a message confirming email was sent
        console.log('Password reset email sent successfully:', result.data.message);
        setIsSubmitted(true);
      } else {
        // Unexpected response format
        console.warn('Unexpected response format:', result);
        setIsSubmitted(true); // Still show success to avoid confusing user
      }
    } catch (error) {
      console.error('Password reset request failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Check Your Email</CardTitle>
          <CardDescription>
            We've sent a password reset link to {email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 items-center">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
              <p className="text-sm text-green-800 font-medium mb-2">
                Password reset email sent successfully!
              </p>
              <p className="text-xs text-green-700">
                Follow the instructions in the email to reset your password and log in with your new credentials.
              </p>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              If you don't see the email, check other places it might be, like your junk, spam, social, or other folders.
            </p>
            <div className="flex flex-col gap-2 w-full">
              <Button asChild className="w-full">
                <Link to="/login">Go to Login</Link>
              </Button>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                }}
              >
                Send Another Email
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Forgot Password</CardTitle>
        <CardDescription>
          Enter your email to reset your password
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            <div className="grid gap-3">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <div className="text-center text-sm">
              Remember your password?{" "}
              <Link to="/login" className="underline underline-offset-4">
                Back to login
              </Link>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
